import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCouponUsageSummary, useCouponUsageUsers } from '../hooks/useCouponUsage.js';
import type { CouponUsageItem } from '../types/coupon-usage.js';

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business クーポン利用状況ページ。マスター別サマリ一覧＋ユーザードリルダウンモーダル。
 * @graph-connects useCouponUsageSummary [calls] サマリ取得
 * @graph-connects useCouponUsageUsers [calls] ユーザー一覧取得
 */
export function CouponUsagePage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useCouponUsageSummary();
  const [selectedCoupon, setSelectedCoupon] = useState<CouponUsageItem | null>(null);
  const { data: usersData, isLoading: usersLoading } = useCouponUsageUsers(
    selectedCoupon?.couponMasterId ?? null,
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-text-muted)]">
        {t('app.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-error)]">
        {t('common.fetchError')}
      </div>
    );
  }

  const coupons = data?.coupons ?? [];
  const totals = data?.totals ?? {
    couponCount: 0,
    issuedCount: 0,
    totalUsageCount: 0,
    paidCount: 0,
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-primary)]">
          {t('coupon.title')}
        </h1>
        <p className="mt-1 text-[13px] text-[var(--color-text-muted)]">{t('coupon.description')}</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        <SummaryCard label={t('coupon.couponCount')} value={totals.couponCount} />
        <SummaryCard label={t('coupon.issuedCodes')} value={totals.issuedCount} />
        <SummaryCard label={t('coupon.claimedCodes')} value={totals.totalUsageCount} accent />
        <SummaryCard label={t('coupon.used')} value={totals.paidCount} />
      </div>

      {/* Table */}
      {coupons.length === 0 ? (
        <div className="flex min-h-[120px] items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[13px] text-[var(--color-text-muted)]">
          {t('coupon.noCoupons')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                <tr>
                  <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t('coupon.couponName')}
                  </th>
                  <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t('coupon.validPeriod')}
                  </th>
                  <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t('common.status')}
                  </th>
                  <th className="px-3.5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t('coupon.issued')}
                  </th>
                  <th className="px-3.5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t('coupon.claimed')}
                  </th>
                  <th className="px-3.5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t('coupon.pendingPayment')}
                  </th>
                  <th className="px-3.5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t('coupon.used')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr
                    key={coupon.couponMasterId}
                    className="cursor-pointer border-b border-[var(--color-border-subtle)] last:border-b-0 hover:bg-[var(--color-bg-elevated)]"
                    onClick={() => {
                      setSelectedCoupon(coupon);
                    }}
                  >
                    <td className="px-3.5 py-2.5">
                      <div className="font-medium text-[var(--color-text-primary)]">
                        {coupon.name}
                      </div>
                      <div className="mt-0.5 text-[12px] text-[var(--color-text-secondary)]">
                        {formatDiscount(coupon, t)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3.5 py-2.5 font-mono text-[12px] tabular-nums text-[var(--color-text-secondary)]">
                      {formatPeriod(coupon.usageStartDate, coupon.usageEndDate, t)}
                    </td>
                    <td className="px-3.5 py-2.5">
                      <CouponStatusBadge
                        startDate={coupon.usageStartDate}
                        endDate={coupon.usageEndDate}
                      />
                    </td>
                    <td className={countCellClass(coupon.issuedCount)}>{coupon.issuedCount}</td>
                    <td className={countCellClass(coupon.totalUsageCount)}>
                      {coupon.totalUsageCount}
                    </td>
                    <td className={countCellClass(coupon.pendingPaymentCount)}>
                      {coupon.pendingPaymentCount}
                    </td>
                    <td className={countCellClass(coupon.paidCount)}>{coupon.paidCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Drilldown Modal */}
      <Modal
        open={selectedCoupon !== null}
        onClose={() => {
          setSelectedCoupon(null);
        }}
        size="lg"
        title={selectedCoupon?.name ?? ''}
        description={t('coupon.modalDescription', {
          discount: formatDiscount(selectedCoupon, t),
          num: String(selectedCoupon?.totalUsageCount ?? 0),
        })}
      >
        {usersLoading ? (
          <div className="flex min-h-[100px] items-center justify-center text-[13px] text-[var(--color-text-muted)]">
            {t('app.loading')}
          </div>
        ) : (usersData?.users ?? []).length === 0 ? (
          <div className="flex min-h-[100px] items-center justify-center text-[13px] text-[var(--color-text-muted)]">
            {t('coupon.noUsers')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                <tr>
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    arthur_user_id
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t('common.status')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t('coupon.claimedAt')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t('coupon.usedAt')}
                  </th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t('coupon.discountAmount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(usersData?.users ?? []).map((user, idx) => (
                  <tr
                    key={`${String(user.arthurUserId)}-${String(idx)}`}
                    className="border-b border-[var(--color-border-subtle)] last:border-b-0"
                  >
                    <td className="px-4 py-2.5">
                      <Link
                        to="/members/$arthurUserId"
                        params={{ arthurUserId: String(user.arthurUserId) }}
                        className="font-medium text-[var(--color-accent-primary)] hover:underline"
                      >
                        {user.arthurUserId}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5">
                      <UsageStatusBadge status={user.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[12px] tabular-nums text-[var(--color-text-secondary)]">
                      {formatDateTime(user.claimedAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[12px] tabular-nums text-[var(--color-text-secondary)]">
                      {user.usedAt ? formatDateTime(user.usedAt) : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-[12px] tabular-nums">
                      {user.appliedAmount !== null
                        ? `¥${user.appliedAmount.toLocaleString()}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}

/** @graph-connects none */
function SummaryCard({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3.5">
      <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--color-text-muted)]">
        {label}
      </div>
      <div
        className={`mt-1.5 text-[28px] font-bold tracking-tight tabular-nums ${
          accent ? 'text-[var(--color-accent-primary)]' : 'text-[var(--color-text-primary)]'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

/** @graph-connects none */
function CouponStatusBadge({
  startDate,
  endDate,
}: {
  startDate: string | null;
  endDate: string | null;
}) {
  const { t } = useTranslation();
  const now = new Date().toISOString().slice(0, 10);
  const start = startDate?.slice(0, 10) ?? '';
  const end = endDate?.slice(0, 10) ?? '';

  let label: string;
  let className: string;

  if (start > now) {
    label = t('coupon.beforeStart');
    className = 'bg-[var(--color-info-bg,rgba(59,130,246,0.1))] text-[var(--color-info)]';
  } else if (end < now && end !== '') {
    label = t('coupon.expired');
    className = 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]';
  } else {
    label = t('coupon.active');
    className = 'bg-[var(--color-success-bg,rgba(16,185,129,0.1))] text-[var(--color-success)]';
  }

  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${className}`}>
      {label}
    </span>
  );
}

/** @graph-connects none */
function UsageStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const map: Record<string, { label: string; className: string }> = {
    claimed: {
      label: t('coupon.acquired'),
      className: 'bg-[var(--color-success-bg,rgba(16,185,129,0.1))] text-[var(--color-success)]',
    },
    pending_payment: {
      label: t('coupon.pendingPayment'),
      className: 'bg-[var(--color-warning-bg,rgba(245,158,11,0.1))] text-[var(--color-warning)]',
    },
    paid: {
      label: t('coupon.used'),
      className: 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]',
    },
  };

  const st = map[status] ?? {
    label: status,
    className: 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]',
  };

  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${st.className}`}
    >
      {st.label}
    </span>
  );
}

/** @graph-connects none */
function formatDiscount(coupon: CouponUsageItem | null, t: TFunction): string {
  if (!coupon) return '';
  const label = t('coupon.monthlyDiscount');
  if (coupon.discountAmount !== null && coupon.discountAmount > 0) {
    return t('coupon.monthlyDiscountAmount', {
      label,
      amount: coupon.discountAmount.toLocaleString(),
    });
  }
  if (coupon.discountRate !== null && coupon.discountRate > 0) {
    return t('coupon.monthlyDiscountRate', { label, rate: String(coupon.discountRate) });
  }
  return coupon.discountDescription ?? '';
}

/** @graph-connects none */
function formatPeriod(startDate: string | null, endDate: string | null, t: TFunction): string {
  const start = startDate ? formatDateShort(startDate) : '—';
  const end = endDate ? formatEndDate(endDate, t) : '—';
  return `${start} 〜 ${end}`;
}

/** @graph-connects none */
function formatDateShort(dateStr: string): string {
  const d = dateStr.slice(0, 10);
  return d.replace(/-/g, '/');
}

/** @graph-connects none */
function formatEndDate(dateStr: string, t: TFunction): string {
  const d = dateStr.slice(0, 10);
  if (d >= '2999-01-01') return t('coupon.unlimited');
  return d.replace(/-/g, '/');
}

/** @graph-connects none */
function formatDateTime(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd} ${hh}:${min}`;
}

/** @graph-connects none */
function countCellClass(count: number): string {
  const base = 'whitespace-nowrap px-3.5 py-2.5 text-right font-mono text-[12px] tabular-nums';
  return count > 0
    ? `${base} font-medium text-[var(--color-text-primary)]`
    : `${base} text-[var(--color-text-muted)]`;
}
